// Four statuses, four colours a field team names without a key
// (owner, 2026-09-14): installed is GREEN because done is green;
// being installed is YELLOW, the work under way; procured is PURPLE,
// the material on site; projected to install is ORANGE, the plan.
//
// This replaced a single-hue green ramp. The ramp read as an ordered
// scale, which these are, but at three pixels on a busy drawing its
// middle steps were two greens nobody could tell apart, and the model
// view paints a whole scope in the first step - a site of pale green
// said "started" when it meant "not started". Four hues are read one
// glance at a time; the dash pattern below still carries the order.
export const STATUS_ORDER = ['projected', 'procured', 'being_installed', 'installed'];

export const STATUS_COLOR = {
  projected: '#E8891A',
  procured: '#7C3AED',
  being_installed: '#E5B800',
  installed: '#1F9D55',
};

// SECOND ENCODING, so status never rests on colour alone.
//
// Four steps of one hue are four greens, and on a busy drawing at three
// pixels wide, mid-green against dark-green is a hard read - for anyone,
// and much harder with a colour vision deficiency or on a photocopied
// sheet taken out to site.
//
// The pattern carries the same order the colour does: a projected run is
// barely dotted, and it fills in as the work does until an installed run
// is a solid unbroken line. The line literally completes.
export const STATUS_DASH = {
  projected: '2 6',
  procured: '8 5',
  being_installed: '14 4',
  installed: '',          // solid - done
};

export const STATUS_LABEL = {
  projected: 'Projected to install',
  procured: 'Procured',
  being_installed: 'Being installed',
  installed: 'Installed',
};

// Short forms for the table's dropdown and the hover popup, where the
// full phrase does not fit.
export const STATUS_SHORT = {
  projected: 'Projected',
  procured: 'Procured',
  being_installed: 'Installing',
  installed: 'Installed',
};

// Systems are the CATEGORY AXIS of the by-system chart, not a colour
// encoding - they are labelled, so they need no hues of their own. These
// are only for the layout overlay, where a run has to be told apart from
// its neighbour at a glance and there is no room for a label.
//
// The first three slots of the categorical palette, which are the three
// that clear the all-pairs floors in both modes; past three the overlay
// falls back to the ink colour rather than inventing a hue.
export const SYSTEM_COLOR = {
  cableTray: '#2a78d6',
  conduit: '#eb6834',
  busDuct: '#1baf7a',
  gear: '#4a3aa7',
  duct: '#eda100',
  pipe: '#e87ba4',
};
export const SYSTEM_FALLBACK = '#52514e';
export const systemColor = (s) => SYSTEM_COLOR[s] || SYSTEM_FALLBACK;

export const SYSTEM_LABEL = {
  cableTray: 'Cable tray',
  conduit: 'Conduit',
  busDuct: 'Bus duct',
  gear: 'Gear',
  duct: 'Duct',
  pipe: 'Pipe',
};
export const systemLabel = (s) => SYSTEM_LABEL[s] || s || 'Other';

// Chart chrome: hairline, solid, one shade off the surface. Never dashed
// - a dashed grid reads as "projection" when it is just a grid.
export const CHART_INK = {
  grid: '#ECECEC',
  axis: '#8D8D8D',
  text: '#3A4952',
  surface: '#ffffff',
};

// How a doc-control sheet names itself in a picker.
//
// The board hands sheets back as DocControl rows, whose fields are
// `sheetNumber` and `sheetName`. The model stage read `name` and
// `number`, which no row has, so every option in its picker rendered as
// the word "Sheet" and the list looked like it had not loaded at all
// (owner, 2026-09-09). One helper, used by both pickers, so they cannot
// disagree about it again.
export const sheetLabel = (s) => {
  if (!s) return '';
  const number = String(s.sheetNumber || '').trim();
  const name = String(s.sheetName || '').trim();
  const revision = String(s.revisionNumber || '').trim();
  const head = [number, name].filter(Boolean).join(' — ');
  if (!head) return 'Sheet';
  return revision ? `${head} (rev ${revision})` : head;
};
